/**
 * Airport Reference Section
 * Settings page section for managing airports and airstrips.
 *
 * Requirements: 18.1, 18.2, 18.3, 18.4, 18.5, 18.6, 18.7, 18.8, 18.9, 18.10
 */

import { createDataGrid } from '../data-grid.js';
import { openDetailModal } from '../detail-modal.js';
import {
  getAirports,
  upsertAirport,
  updateAirport,
  deleteAirport,
} from '../../services/api.js';
import { validateAirportPayload } from '../../utils/validation.js';

// ── Airport type options ────────────────────────────────────────────────────

const AIRPORT_TYPES = [
  { value: 'large_airport', label: 'Large Airport' },
  { value: 'medium_airport', label: 'Medium Airport' },
  { value: 'small_airport', label: 'Small Airport' },
  { value: 'airstrip', label: 'Airstrip' },
  { value: 'heliport', label: 'Heliport' },
  { value: 'seaplane_base', label: 'Seaplane Base' },
];

// ── Pure helper (exported for testing) ──────────────────────────────────────

/**
 * Upsert logic: if a code already exists in the list, return the existing
 * record's id so the caller can update rather than duplicate.
 * @param {string} code
 * @param {Object[]} airports
 * @returns {Object|null} existing airport or null
 */
export function findExistingByCode(code, airports) {
  if (!code) return null;
  const normalised = code.trim().toUpperCase();
  return airports.find((a) => (a.icao_code || '').trim().toUpperCase() === normalised) || null;
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * createAirportReference() → HTMLElement
 *
 * Returns a section element containing the airport reference Data_Grid
 * with add (upsert), inline edit, and delete capabilities.
 */
export function createAirportReference() {
  const section = document.createElement('section');
  section.className = 'settings-section airport-reference';

  /** @type {Object[]} */
  let airports = [];

  const gridContainer = document.createElement('div');
  section.appendChild(gridContainer);

  // Message area for success/error feedback
  const messageArea = document.createElement('div');
  messageArea.className = 'airport-reference__message';
  section.appendChild(messageArea);

  /**
   * Show a temporary message.
   * @param {string} text
   * @param {'success'|'error'} type
   */
  function showMessage(text, type = 'success') {
    messageArea.textContent = text;
    messageArea.className = `airport-reference__message airport-reference__message--${type}`;
    setTimeout(() => {
      messageArea.textContent = '';
      messageArea.className = 'airport-reference__message';
    }, 4000);
  }

  /**
   * Build and render the Data_Grid with current airport data.
   */
  function renderGrid() {
    gridContainer.innerHTML = '';

    const grid = createDataGrid({
      columns: [
        { key: 'icao_code', label: 'Code' },
        { key: 'name', label: 'Name' },
        { key: 'country_code', label: 'Country' },
        {
          key: 'type',
          label: 'Type',
          format: (v) => {
            const opt = AIRPORT_TYPES.find((t) => t.value === v);
            return opt ? opt.label : v || 'Small Airport';
          },
        },
      ],
      data: airports,
      defaultSort: 'icao_code',
      defaultOrder: 'asc',
      emptyMessage:
        'No airports or airstrips configured. Use the seed script or add entries manually.',
      filterable: true,
      heading: 'Airports & Airstrips',
      addLabel: 'Add Airport/Airstrip',
      onAdd: () => openAddAirportModal(),
      onRowClick: (row) => openEditAirportModal(row),
    });

    gridContainer.appendChild(grid);
  }

  /**
   * Open the "Add Airport/Airstrip" modal.
   * Uses upsert behaviour: if code already exists, updates the existing record.
   */
  function openAddAirportModal() {
    openDetailModal({
      title: 'Add Airport/Airstrip',
      fields: [
        { name: 'code', label: 'Code / Identifier', type: 'text', required: true },
        { name: 'name', label: 'Name', type: 'text', required: true },
        { name: 'latitude', label: 'Latitude', type: 'number' },
        { name: 'longitude', label: 'Longitude', type: 'number' },
        { name: 'country_code', label: 'Country Code', type: 'text' },
        {
          name: 'type',
          label: 'Type',
          type: 'select',
          options: AIRPORT_TYPES,
        },
      ],
      onSave: async (formData) => {
        // Validate
        const validation = validateAirportPayload({
          code: formData.code,
          name: formData.name,
        });
        if (!validation.valid) {
          const err = new Error(validation.errors[0]);
          err.fieldErrors = {};
          validation.errors.forEach((e) => {
            if (e.includes('code')) err.fieldErrors.code = e;
            else if (e.includes('name')) err.fieldErrors.name = e;
          });
          throw err;
        }

        const payload = {
          icao_code: formData.code.trim(),
          name: formData.name.trim(),
        };
        if (formData.latitude) payload.latitude = Number(formData.latitude);
        if (formData.longitude) payload.longitude = Number(formData.longitude);
        if (formData.country_code) payload.country_code = formData.country_code.trim();
        if (formData.type) payload.type = formData.type;

        // Upsert: update if code exists, insert otherwise
        await upsertAirport(payload);
        await refreshAirports();
        const existing = findExistingByCode(formData.code, airports);
        showMessage(existing ? 'Airport updated (existing code)' : 'Airport added successfully');
      },
    });
  }

  /**
   * Open the edit modal for an existing airport.
   * @param {Object} airport
   */
  function openEditAirportModal(airport) {
    openDetailModal({
      title: `Edit Airport — ${airport.icao_code}`,
      fields: [
        { name: 'icao_code', label: 'Code / Identifier', type: 'text', required: true },
        { name: 'name', label: 'Name', type: 'text', required: true },
        { name: 'latitude', label: 'Latitude', type: 'number' },
        { name: 'longitude', label: 'Longitude', type: 'number' },
        { name: 'country_code', label: 'Country Code', type: 'text' },
        {
          name: 'type',
          label: 'Type',
          type: 'select',
          options: AIRPORT_TYPES,
        },
      ],
      values: {
        icao_code: airport.icao_code,
        name: airport.name,
        latitude: airport.latitude,
        longitude: airport.longitude,
        country_code: airport.country_code,
        type: airport.type || 'small_airport',
      },
      onSave: async (formData) => {
        // Validate
        const validation = validateAirportPayload({
          code: formData.icao_code,
          name: formData.name,
        });
        if (!validation.valid) {
          const err = new Error(validation.errors[0]);
          err.fieldErrors = {};
          validation.errors.forEach((e) => {
            if (e.includes('code')) err.fieldErrors.icao_code = e;
            else if (e.includes('name')) err.fieldErrors.name = e;
          });
          throw err;
        }

        const updates = {
          icao_code: formData.icao_code.trim(),
          name: formData.name.trim(),
          latitude: formData.latitude ? Number(formData.latitude) : airport.latitude,
          longitude: formData.longitude ? Number(formData.longitude) : airport.longitude,
          country_code: formData.country_code ? formData.country_code.trim() : airport.country_code,
          type: formData.type || airport.type,
        };

        await updateAirport(airport.id, updates);
        await refreshAirports();
        showMessage('Airport updated successfully');
      },
      onDelete: async () => {
        const identifier = airport.icao_code || airport.name;
        if (!window.confirm(`Are you sure you want to delete ${identifier}?`)) {
          throw new Error('Cancelled');
        }
        try {
          await deleteAirport(airport.id);
          await refreshAirports();
          showMessage(`${identifier} deleted`);
        } catch (err) {
          const msg = err.message || '';
          if (msg.includes('23503') || msg.toLowerCase().includes('foreign key')) {
            throw new Error(`Cannot delete ${identifier}: it is referenced by other records`);
          }
          throw err;
        }
      },
    });
  }

  /**
   * Fetch airports from the API and re-render the grid.
   */
  async function refreshAirports() {
    try {
      airports = await getAirports();
    } catch (err) {
      airports = [];
      showMessage('Failed to load airports', 'error');
    }
    renderGrid();
  }

  // Initial load
  refreshAirports();

  return section;
}
